import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  fetchCalculatorRates,
  fetchReferencePrice,
} from '../../store/slices/calculatorSlice';
import { applyTier, computeEstimate } from './calc';
import { buildShareParams, parseShareParams } from './urlState';
import { capture } from '../../analytics';
import styles from './GridCostCalculator.module.css';

const KNOB_KEYS = [
  'overtime_multiplier', 'crew_min_call_hours', 'onset_base_hours',
  'onset_minutes_per_lens', 'post_hours_per_lens', 'overhead_pct',
];
// Budget presets live in Advanced. Each one seeds the editable rate fields with
// base x factor (a starting point the user then fine-tunes) rather than acting as a
// live scalar layered on top — so there is a single source of truth (the rates) and
// the simple view always shows the honest non-union commercial baseline. The factors
// reuse the per-market tier_* fields, so they stay admin-editable.
const PRESETS = [
  ['indie', 'Low-budget', 'tier_indie'],
  ['commercial', 'Commercial', 'tier_commercial'],
  ['highend', 'Union / studio', 'tier_highend'],
];

const SCENARIO_HELP = {
  addon: 'Grids shot right after the main shoot wraps: you keep the gear and crew and pay overtime.',
  standalone: 'A separate session booked just for grids: full day rates for stage, camera and crew.',
};

const KNOB_META = {
  // "2 x base" reads as double pay; a distinct name avoids colliding with the
  // "Add-on overtime" scenario button (which picks the mode, not the pay factor).
  overtime_multiplier: { label: 'Overtime pay', unit: '× base', step: '0.1' },
  crew_min_call_hours: { label: 'Min. crew call', unit: 'hrs', step: '1' },
  onset_base_hours: { label: 'Setup & teardown', unit: 'hrs', step: '0.5' },
  onset_minutes_per_lens: { label: 'Minutes per lens', unit: 'min', step: '1' },
  post_hours_per_lens: { label: 'Hours per lens', unit: 'hrs', step: '0.5' },
  overhead_pct: { label: 'Added to the total', unit: '%', step: '0.5' },
};

// Compact on-screen units. The DB `unit` is verbose ("per shift per lens"); rates read
// cleaner as a short suffix next to the number ("150 /day").
const RATE_UNIT = { per_hour: '/hr', per_shift: '/day', per_shift_per_lens: '/day' };
// Shorter labels for a couple of long rate names — the section heading already supplies
// the context (e.g. "On-set VFX supervisor" -> "VFX supervisor" under On-set crew).
const RATE_LABEL = {
  mechanic_1ac: 'Camera mechanic',
  onset_supervisor: 'On-set supervisor',
  matchmove_artist: 'Matchmove artist',
};

// A short caption sits directly under the line it clarifies. Only the supervisor needs
// one: its "/day" tag looks odd next to the hourly crew, so this flags that it's billed
// by the hour anyway. The full "why" lives in the methodology ("How this is calculated").
const RATE_HINT = {
  onset_supervisor: 'A day rate, billed by the hour.',
};

// Advanced fields grouped by meaning, so a field sits where a user would look for it.
// Standalone minimums live with what they gate (stage->rental, crew->crew); the per-lens
// time knobs live with what they lengthen (shoot minutes->crew, post hours->post).
const SECTIONS = [
  { head: 'Gear rental', rates: ['studio_stage', 'camera_package', 'lens_each'], knobs: [] },
  { head: 'On-set crew', rates: ['mechanic_1ac', 'electrician', 'dit_playback', 'onset_supervisor'], knobs: ['crew_min_call_hours', 'overtime_multiplier', 'onset_base_hours', 'onset_minutes_per_lens'] },
  { head: 'Post', rates: ['matchmove_artist'], knobs: ['post_hours_per_lens'] },
  { head: 'Overhead', rates: [], knobs: ['overhead_pct'] },
];

// Advanced is a "what am I paying for" checklist: every priced rental/crew line can be
// unticked (free / already owned / a role not on the shoot). The checklist is identical in
// both scenarios; only the defaults differ, and that difference is what each scenario means.
// Add-on: gear is free (already on the main shoot) and the whole crew stayed -> gear off,
// crew on. Standalone: a fresh booking pays for gear and the whole crew you'd hire for the
// day. The on-set supervisor is on by default in both — grids are rarely shot without a
// studio representative running the VFX brief (in add-on they bill only their overtime hours,
// in standalone the crew minimum call). The estimate leans slightly high on purpose — the
// checklist lets the user untick anything they don't need, the safer direction for a "DIY is
// expensive" tool. Post is never toggleable.
const PAY_DEFAULTS = {
  addon: {
    studio_stage: false, camera_package: false, lens_each: false,
    mechanic_1ac: true, electrician: true, dit_playback: true, onset_supervisor: true,
  },
  standalone: {
    studio_stage: true, camera_package: true, lens_each: true,
    mechanic_1ac: true, electrician: true, dit_playback: true, onset_supervisor: true,
  },
};

// On-brand market picker: a native <select> can't style its option list, so this
// is a small custom listbox. Closes on select, Escape, or an outside click.
function MarketSelect({ profiles, activeProfile, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Every market is priced in USD now, so the currency tag would just repeat "(USD)"
  // on every row — the market name alone is enough.
  const label = (p) => p.name;

  return (
    <div className={styles.selectBox} ref={ref}>
      <button
        type="button"
        className={styles.selectTrigger}
        aria-label="Market"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{label(activeProfile)}</span>
        <i className={styles.selectChevron} aria-hidden="true" />
      </button>
      {open && (
        <ul className={styles.selectList} role="listbox">
          {profiles.map((p) => (
            <li
              key={p.id}
              role="option"
              aria-selected={p.id === activeProfile.id}
              className={`${styles.selectOption} ${p.id === activeProfile.id ? styles.selectOptionActive : ''}`}
              onClick={() => { onChange(p.id); setOpen(false); }}
            >
              {label(p)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// navigator.clipboard needs a secure context; fall back to the legacy textarea
// trick for older browsers. Fire-and-forget: the "Copied!" label is optimistic.
const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch { /* old browser, nothing to do */ }
  document.body.removeChild(ta);
};

export default function GridCostCalculator() {
  const dispatch = useDispatch();
  const { profiles, referencePrice, status } = useSelector((s) => s.calculator);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [scenario, setScenario] = useState('addon');
  const [lensCount, setLensCount] = useState(3);
  const [advanced, setAdvanced] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [overrides, setOverrides] = useState({});          // rate key -> number
  const [knobOverrides, setKnobOverrides] = useState({});  // knob key -> number
  // Which budget preset is currently "clean" (highlighted). Commercial is the baseline;
  // any manual rate edit makes it "custom" (null), since it no longer matches a preset.
  const [activePreset, setActivePreset] = useState('commercial');
  // Pay-for checklist: which priced lines the user is actually paying for. Reset to the
  // new scenario's defaults on switch (see chooseScenario), so nothing leaks across.
  const [paying, setPaying] = useState(PAY_DEFAULTS.addon);
  const resultRef = useRef(null);
  const [frozenResultH, setFrozenResultH] = useState(null);
  const [copied, setCopied] = useState(false);

  // Opening Advanced grows the controls column; freeze the result at its current
  // (equal-height) size first, so the pinned panel keeps the total exactly where
  // it is instead of jumping. On close, hold the freeze until the controls column
  // has finished collapsing (matches the .advReveal transition), then release —
  // otherwise the result stretches to the still-tall column mid-animation and jumps.
  const collapseTimer = useRef(null);
  const copyTimer = useRef(null);
  const toggleAdvanced = () => {
    if (collapseTimer.current) { clearTimeout(collapseTimer.current); collapseTimer.current = null; }
    if (!advanced) {
      setFrozenResultH(resultRef.current ? resultRef.current.offsetHeight : null);
      setAdvanced(true);
      capture('calculator_advanced_opened', {
        scenario, lens_count: lensCount, market: activeProfile?.currency_code,
      });
    } else {
      setAdvanced(false);
      collapseTimer.current = setTimeout(() => setFrozenResultH(null), 360);
    }
  };
  useEffect(() => () => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  useEffect(() => {
    dispatch(fetchCalculatorRates());
    dispatch(fetchReferencePrice());
  }, [dispatch]);

  // Restore a shared estimate from the URL — once, when the profiles arrive (the
  // market is matched by profile name). After this, state flows the other way:
  // into the URL (see the sync effect below).
  const didInitFromUrl = useRef(false);
  useEffect(() => {
    if (didInitFromUrl.current || !profiles.length) return;
    didInitFromUrl.current = true;
    const shared = parseShareParams(searchParams);
    const scenario = shared.scenario || 'addon';
    if (shared.scenario) setScenario(shared.scenario);
    const pay = { ...PAY_DEFAULTS[scenario] };
    (shared.payFlip || []).forEach((key) => {
      if (key in pay) pay[key] = !pay[key];
    });
    if (shared.scenario || shared.payFlip) setPaying(pay);
    if (shared.lensCount != null) setLensCount(shared.lensCount);
    const marketProfile = shared.market
      ? profiles.find((pr) => pr.name.toLowerCase() === shared.market)
      : null;
    if (marketProfile) setProfileId(marketProfile.id);
    const profile = marketProfile || profiles.find((pr) => pr.is_default) || profiles[0];
    // A named preset re-seeds the rates from the resolved profile; a custom set (r)
    // restores verbatim. preset=commercial needs nothing — it is the pristine base.
    if (shared.preset && shared.preset !== 'commercial') {
      const field = PRESETS.find(([key]) => key === shared.preset)?.[2];
      if (field) {
        const factor = Number(profile[field]);
        if (factor === 1) {
          setOverrides({});
        } else {
          const scaled = applyTier(profile.items, factor);
          const seeded = {};
          profile.items.forEach((it) => {
            if (it.group === 'post') return;
            seeded[it.key] = Math.round(scaled[it.key] * 100) / 100;
          });
          setOverrides(seeded);
        }
        setActivePreset(shared.preset);
      }
    } else if (shared.rates) {
      setOverrides(shared.rates);
      setActivePreset(null);
    }
    if (shared.knobs) setKnobOverrides(shared.knobs);
  }, [profiles, searchParams]);

  const activeProfile = useMemo(() => {
    if (!profiles.length) return null;
    if (profileId) return profiles.find((p) => p.id === profileId) || profiles[0];
    return profiles.find((p) => p.is_default) || profiles[0];
  }, [profiles, profileId]);

  const estimate = useMemo(() => {
    if (!activeProfile) return null;
    // Base rates are the commercial baseline (no live scalar). A budget preset or a
    // manual edit made in Advanced lands in `overrides`/`knobOverrides` and keeps
    // applying after the panel is closed — same as the pay-for checklist, which
    // already applies in both views. The simple view shows the pristine baseline
    // only until the visitor customizes something.
    const rates = {};
    activeProfile.items.forEach((it) => {
      const val = overrides[it.key] != null ? Number(overrides[it.key]) : Number(it.amount);
      // Pay-for checklist: an unticked line contributes 0 in either scenario.
      const unpaid = it.key in paying && !paying[it.key];
      rates[it.key] = unpaid ? 0 : val;
    });
    const knobs = {};
    KNOB_KEYS.forEach((k) => {
      knobs[k] = knobOverrides[k] != null ? Number(knobOverrides[k]) : Number(activeProfile[k]);
    });
    return computeEstimate({ scenario, lensCount, rates, knobs });
  }, [activeProfile, overrides, knobOverrides, paying, scenario, lensCount]);

  // The shareable form of the current estimate. Used both to keep the address bar
  // in sync and to build the copied link — one source for both.
  const shareParams = useMemo(() => (
    activeProfile ? buildShareParams({
      scenario, lensCount, profile: activeProfile, activePreset,
      overrides, knobOverrides, paying, payingDefaults: PAY_DEFAULTS[scenario],
    }) : null
  ), [activeProfile, scenario, lensCount, activePreset, overrides, knobOverrides, paying]);

  // Keep the address bar in sync with the estimate so the URL can be copied or
  // shared at any moment. replace (not push): dragging the lens slider shouldn't
  // spam the history stack. Skipped until the incoming URL has been applied.
  useEffect(() => {
    if (!didInitFromUrl.current || shareParams === null) return;
    if (shareParams.toString() !== searchParams.toString()) {
      setSearchParams(shareParams, { replace: true });
    }
  }, [shareParams, searchParams, setSearchParams]);

  // The reference-price fetch failing is handled silently (fallback_preset_price
  // kicks in), but without the rates there is nothing to calculate on — say so
  // instead of spinning "Loading" forever.
  if (status === 'failed') {
    return (
      <div className={styles.loading} role="alert">
        Couldn’t load the calculator rates. Check your connection and refresh the page.
      </div>
    );
  }

  if (status !== 'succeeded' || !activeProfile || !estimate) {
    return <div className={styles.loading}>Loading calculator…</div>;
  }

  const sym = activeProfile.currency_symbol;
  const fmt = (n) => `${sym}${Math.round(n).toLocaleString()}`;
  // The DIY total includes a matchmove artist building the preset, so the honest
  // headline compares against a full product (grids + preset), not the bare grids.
  // Only usable when the profile currency matches the catalog (USD); a non-positive
  // catalog value (e.g. everything priced 0) falls back to the profile's
  // representative price so the punchline never shows "0 products".
  const usd = referencePrice && activeProfile.currency_code === referencePrice.currency;
  const fullCatalog = usd ? referencePrice.min_full_price : null;
  const fullPrice = fullCatalog > 0 ? fullCatalog : activeProfile.fallback_preset_price;
  // Head-to-head: buying the same coverage is lensCount full products (each = one lens's
  // grid + preset). min_full_price is the cheapest full-product price in the catalog.
  const buyTotal = Math.max(0, lensCount) * Number(fullPrice);
  // Bar segments are shares of the full total (which includes overhead when set).
  const pct = (v) => (estimate.total > 0 ? (v / estimate.total) * 100 : 0);

  const switchProfile = (id) => {
    setProfileId(id);
    setOverrides({});
    setKnobOverrides({});
    setActivePreset('commercial');
  };

  // Seed the editable rate fields from a budget preset: base x factor for every
  // rental/crew line, post left untouched (a matchmove artist bills the same at any
  // budget). This writes real rates into `overrides`, so the user can fine-tune from
  // there — the preset is a starting point, not a scalar that lingers.
  const applyPreset = (key, field) => {
    setActivePreset(key);
    const factor = Number(activeProfile[field]);
    // A x1 preset (Commercial by default) would seed values equal to the base rates,
    // marking the set as customized without changing a single number — clear instead.
    if (factor === 1) {
      setOverrides({});
      return;
    }
    const scaled = applyTier(activeProfile.items, factor);
    const seeded = {};
    activeProfile.items.forEach((it) => {
      if (it.group === 'post') return;
      seeded[it.key] = Math.round(scaled[it.key] * 100) / 100;
    });
    setOverrides(seeded);
  };

  // A manual rate edit no longer matches any preset -> mark the set as custom.
  const editRate = (key, value) => {
    setActivePreset(null);
    setOverrides((o) => ({ ...o, [key]: value }));
  };

  // Switching scenario resets the pay-for checklist to that scenario's defaults, so the
  // add-on assumptions (gear free) never leak into a standalone estimate and vice versa.
  const chooseScenario = (s) => {
    setScenario(s);
    setPaying(PAY_DEFAULTS[s]);
  };

  // Reset the whole estimate back to its pristine defaults. Wired to the eyebrow link:
  // a plain click resets in place (no reload; the URL-sync effect then clears the shared
  // params), while the href keeps cmd-click / middle-click opening a fresh calculator.
  const resetCalculator = (e) => {
    e.preventDefault();
    setScenario('addon');
    setLensCount(3);
    setProfileId(null);
    setOverrides({});
    setKnobOverrides({});
    setActivePreset('commercial');
    setPaying(PAY_DEFAULTS.addon);
    setAdvanced(false);
    if (collapseTimer.current) { clearTimeout(collapseTimer.current); collapseTimer.current = null; }
    setFrozenResultH(null);
    capture('calculator_reset', { via: 'eyebrow' });
  };

  // Whole hours read cleaner than "6.0"; keep one decimal only when it carries info.
  const fmtHrs = (h) => {
    const r = Math.round(h * 10) / 10;
    return Number.isInteger(r) ? String(r) : r.toFixed(1);
  };
  const roundedHours = Math.round(estimate.totalHours * 10) / 10;
  const hoursLabel = fmtHrs(estimate.totalHours);
  const hoursWord = roundedHours === 1 ? 'hour' : 'hours';
  // The headline hours sum two different people at two different rates: on-set crew
  // (mechanic/electrician/DIT) and a post artist working alone later. Show the split so
  // the figure next to the price can't read as "N crew-hours for $X" — e.g. a matchmove
  // artist priced at 0 drops out of the money but still adds its hours to this total.
  const hoursParts = [];
  if (estimate.onsetHours > 0) hoursParts.push(`${fmtHrs(estimate.onsetHours)} on set`);
  if (estimate.postHours > 0) hoursParts.push(`${fmtHrs(estimate.postHours)} in post`);
  const hoursBreakdown = hoursParts.join(' · ');

  const contextLabel = activeProfile.name;
  // Any preset seed or manual edit (rates or timing) marks the estimate as customized;
  // used only for analytics (the `preset` prop on CTA events), not shown in the UI.
  const customized = Object.keys(overrides).length > 0 || Object.keys(knobOverrides).length > 0;

  // Conversion signal: which CTA sends a visitor from the calculator to the library,
  // and with what estimate on screen (money the tool showed, presets it promised).
  const trackCtaClick = (link) => capture('calculator_cta_clicked', {
    link,
    scenario,
    mode: advanced ? 'advanced' : 'simple',
    preset: customized ? (activePreset ?? 'custom') : 'commercial',
    market: activeProfile.currency_code,
    lens_count: lensCount,
    estimate_total: Math.round(estimate.total),
    buy_total: Math.round(buyTotal),
  });

  // Copy the share link for the current estimate. Built from `shareParams` (the same
  // source as the address-bar sync), not from location.href, so it works under any
  // router and never lags a state change behind.
  const shareEstimate = () => {
    const qs = shareParams ? shareParams.toString() : '';
    const url = `${window.location.origin}${location.pathname}${qs ? `?${qs}` : ''}`;
    copyToClipboard(url);
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1800);
    capture('calculator_share_clicked', {
      scenario,
      mode: advanced ? 'advanced' : 'simple',
      preset: customized ? (activePreset ?? 'custom') : 'commercial',
      market: activeProfile.currency_code,
      lens_count: lensCount,
      estimate_total: Math.round(estimate.total),
    });
  };

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Lens Distortion Grid Cost Calculator – Distortion Grids Database</title>
        <meta
          name="description"
          content="Estimate the real cost and time of shooting lens distortion grids yourself: camera and lens rental, crew and post. Then compare it against buying the same grids ready-made."
        />
      </Helmet>

      <div className={styles.inner}>
        <header className={styles.intro}>
          <div className={styles.introMain}>
            <a
              className={styles.eyebrow}
              href="/grid-cost-calculator"
              onClick={resetCalculator}
              title="Reset the calculator"
            >
              Cost &amp; time estimator
            </a>
            <h1 className={styles.title}>What does shooting distortion grids really cost?</h1>
          </div>
          <div className={styles.introAside}>
            <p className={styles.lede}>
              Shooting your own grids takes time and money. Calculate the real cost of your next shoot using standard 2026 rates,
              then see what the same grids cost ready-made on our site. You can change any number below.
            </p>
            {/*<p className={styles.heroHelp}>*/}
            {/*  Built from real 2026 market rates. Set your shoot up below; every number is editable.*/}
            {/*</p>*/}
          </div>
        </header>

        <div className={styles.layout}>
          <section className={styles.controls} aria-label="Shoot setup">
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Scenario</span>
              <div className={styles.seg}>
                <button aria-pressed={scenario === 'addon'} onClick={() => chooseScenario('addon')}>
                  Add-on overtime
                </button>
                <button aria-pressed={scenario === 'standalone'} onClick={() => chooseScenario('standalone')}>
                  Standalone shoot
                </button>
              </div>
              <p className={`${styles.fieldHelp} ${styles.scenarioHelp}`}>{SCENARIO_HELP[scenario]}</p>
            </div>

            <div className={styles.field}>
              <div className={styles.lensHead}>
                <span className={styles.fieldLabel}>Lenses</span>
                <span className={styles.lensVal}>{lensCount}</span>
              </div>
              <input
                className={styles.slider}
                type="range" min="1" max="20" value={lensCount}
                aria-label="Number of lenses"
                onChange={(e) => setLensCount(Number(e.target.value))}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Market</span>
              <MarketSelect
                profiles={profiles}
                activeProfile={activeProfile}
                onChange={switchProfile}
              />
            </div>

            <div className={styles.advGroup}>
            <button className={styles.advToggle} onClick={toggleAdvanced}>
              {advanced ? 'Advanced: Hide' : 'Advanced: Enter your own rates'}
            </button>

            <div
              className={`${styles.advReveal} ${advanced ? styles.advOpen : ''}`}
              aria-hidden={!advanced}
            >
              <div className={styles.advRevealInner}>
                <div className={styles.advPanel}>
                <p className={styles.advIntro}>
                  Every rate below is editable. Overwrite any with your own quote and the
                  total updates live.
                </p>
                <p className={styles.advIntro}>
                  Use the checkboxes to count only what you're paying for. Untick anything
                  free or already owned.
                </p>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Start from a budget level</span>
                  <div className={`${styles.seg} ${styles.presetSeg}`}>
                    {PRESETS.map(([key, label, field]) => (
                      <button
                        key={key}
                        aria-pressed={activePreset === key}
                        onClick={() => applyPreset(key, field)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className={styles.fieldHelp}>
                    Default is non-union commercial. Pick Low-budget for favor rates, or
                    Union / studio for a union crew, then fine-tune any rate below.
                  </p>
                </div>

                {SECTIONS.map((section) => (
                  <div key={section.head} className={styles.rateGroup}>
                    <p className={styles.groupHead}>{section.head}</p>
                    {section.rates.map((key) => {
                      const it = activeProfile.items.find((x) => x.key === key);
                      if (!it) return null;
                      // Rental/crew lines are pay-for toggles; post is always billed.
                      const toggleable = key in paying;
                      const on = paying[key];
                      return (
                        <Fragment key={key}>
                          <div
                            className={`${styles.rateRow} ${toggleable && !on ? styles.rateRowOff : ''}`}
                          >
                            <span className={styles.rowLabel}>
                              {toggleable && (
                                <input
                                  type="checkbox"
                                  className={styles.rowCheck}
                                  checked={on}
                                  aria-label={`Pay for ${it.label}`}
                                  onChange={(e) => setPaying((s) => ({ ...s, [key]: e.target.checked }))}
                                />
                              )}
                              {RATE_LABEL[key] || it.label}
                            </span>
                            <span className={styles.rowField}>
                              <input
                                className={styles.numInput}
                                type="number" min="0" step="any"
                                value={overrides[key] ?? Number(it.amount)}
                                onChange={(e) => editRate(key, e.target.value)}
                              />
                              <em className={styles.rowUnit}>{RATE_UNIT[it.unit] || ''}</em>
                            </span>
                          </div>
                          {RATE_HINT[key] && (
                            <p className={styles.rowHint}>{RATE_HINT[key]}</p>
                          )}
                        </Fragment>
                      );
                    })}
                    {section.knobs.map((key) => (
                      <div key={key} className={styles.rateRow}>
                        <span className={styles.rowLabel}>{KNOB_META[key].label}</span>
                        <span className={styles.rowField}>
                          <input
                            className={styles.numInput}
                            type="number" min="0" step={KNOB_META[key].step}
                            value={knobOverrides[key] ?? Number(activeProfile[key])}
                            onChange={(e) => setKnobOverrides((o) => ({ ...o, [key]: e.target.value }))}
                          />
                          <em className={styles.rowUnit}>{KNOB_META[key].unit}</em>
                        </span>
                      </div>
                    ))}
                    {section.head === 'On-set crew' && (
                      <p className={styles.checkHint}>
                        Setup &amp; teardown is one-time; minutes per lens is the shoot plus
                        re-positioning for the next. Min. crew call applies to a standalone
                        shoot, overtime to add-on.
                      </p>
                    )}
                    {section.head === 'Overhead' && (
                      <p className={styles.checkHint}>
                        Extra costs like insurance or transport. It varies by job, so it
                        starts at 0.
                      </p>
                    )}
                  </div>
                ))}
                </div>
              </div>
            </div>
            </div>
          </section>

          <section
            ref={resultRef}
            className={`${styles.versus} ${frozenResultH ? styles.resultPinned : ''}`}
            style={frozenResultH ? { height: frozenResultH } : undefined}
          >
            <div className={styles.vsGrid}>
              <div className={styles.diySide}>
                <p className={styles.sideLabel}>Shoot it yourself</p>
                <div className={styles.total}>{fmt(estimate.total)}</div>
                <p className={styles.timeMetric}>≈ {hoursLabel} {hoursWord} of work</p>
                {hoursBreakdown && (
                  <p className={styles.timeBreakdown}>{hoursBreakdown}</p>
                )}
                <p className={styles.contextLine}>
                  {lensCount} {lensCount === 1 ? 'lens' : 'lenses'}, {contextLabel}
                </p>

                <div className={styles.breakdown}>
                  <div className={styles.bar}>
                    {estimate.rental > 0 && (
                      <i className={styles.segRental} style={{ width: `${pct(estimate.rental)}%` }} />
                    )}
                    <i className={styles.segLabor} style={{ width: `${pct(estimate.labor)}%` }} />
                    <i className={styles.segPost} style={{ width: `${pct(estimate.post)}%` }} />
                    {estimate.overhead > 0 && (
                      <i className={styles.segOverhead} style={{ width: `${pct(estimate.overhead)}%` }} />
                    )}
                  </div>
                  <ul className={styles.rows}>
                    {estimate.rental > 0 && (
                      <li className={styles.rRental}>Rental <b>{fmt(estimate.rental)}</b></li>
                    )}
                    <li className={styles.rLabor}>Crew <b>{fmt(estimate.labor)}</b></li>
                    <li className={styles.rPost}>Post <b>{fmt(estimate.post)}</b></li>
                    {estimate.overhead > 0 && (
                      <li className={styles.rOverhead}>Overhead <b>{fmt(estimate.overhead)}</b></li>
                    )}
                  </ul>
                </div>
              </div>

              <div className={styles.vsDivider} aria-hidden="true" />

              <div className={styles.buySide}>
                <p className={styles.sideLabel}>Buy it from us</p>
                <div className={styles.buyTotal}>{fmt(buyTotal)}</div>
                <p className={styles.buySub}>
                  for {lensCount} ready-made {lensCount === 1 ? 'grid' : 'grids'} with guaranteed quality
                </p>
                <p className={styles.buyDelivery}>Instant download</p>
                <a
                  className={styles.cta}
                  href="/"
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackCtaClick('primary')}
                >
                  Browse the library
                </a>
              </div>
            </div>

            {/* Share hangs just below the card as an absolute tail: out of the card's
                height (so the card stays equal to the controls) yet anchored to it, so
                it follows when the card pins on scroll with Advanced open. */}
            <button type="button" className={styles.shareLink} onClick={shareEstimate}>
              <svg className={styles.shareLinkIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5"
                  fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
              {copied ? 'Link copied!' : 'Share this estimate'}
            </button>
          </section>
        </div>

        <details className={styles.method}>
          <summary className={styles.methodSummary}>
            <span>How this is calculated</span>
            <i className={styles.methodChevron} aria-hidden="true" />
          </summary>
          <div className={styles.methodBody}>
            <div>
              <h2 className={styles.methodHead}>What goes into the number</h2>
              <p>We add up the hours of work and multiply them by real day rates, in three parts:</p>
              <ul>
                <li><b>Rental</b>: the stage, camera body and each lens.</li>
                <li><b>Crew</b>: the people on set, paid by the hour.</li>
                <li><b>Post</b>: a matchmove artist who builds the grids after the shoot.</li>
              </ul>
              <p>
                The more lenses you shoot, the more it costs: more minutes on set and more hours
                in post. Add-on keeps your crew and gear after the main shoot wraps and pays them
                overtime. Standalone books a fresh session at full day rates.
              </p>
              <p>
                Overtime is based on a standard 12-hour shift: a day rate ÷ 12 is the hourly rate,
                and each overtime hour is billed at 1.5×. Setup and teardown aren’t billed as a
                separate line. They’re folded into the on-set time, so they’re paid as overtime
                in add-on and covered by the crew’s minimum call in standalone.
              </p>
              <p>
                The on-set supervisor is quoted as a day rate, not an hourly rate like the rest of
                the crew, so that’s how you enter it: the number you’d actually get from them. The
                tool does the rest. It splits that day rate into an hourly (divide by 12) and counts
                only the hours they spend on the grids, so you pay for their time on the shoot, not
                a whole extra day.
              </p>
            </div>

            <div>
              <h2 className={styles.methodHead}>Make it match your shoot</h2>
              <p>Open the Advanced settings to turn this into your own number:</p>
              <ul>
                <li>Pick your market and currency.</li>
                <li>Replace any rate with a quote you already have.</li>
                <li>Set the number of lenses, and untick any crew or gear you won’t pay for.</li>
                <li>Adjust the timing if your shoot runs faster or slower.</li>
              </ul>
              <p>The total updates as you type.</p>
            </div>

            <div>
              <h2 className={styles.methodHead}>How accurate is it?</h2>
              <p>
                Think of this as a good estimate, not a final price. Our default numbers are based
                on the average 2026 commercial rates. Real prices will depend on your country,
                studio, and season.
              </p>
              <p>
                If you work on high-end or union projects, increase the rates in the Advanced
                settings. Our goal is to give you a clear picture of the real cost, so you can
                easily compare the cost of shooting grids yourself with that of buying them
                ready-made.
              </p>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
